/**
 * Releases + their downloadable files, as this portal's BFF exposes them.
 *
 * NO URL EVER COMES BACK FROM A LISTING. `listArtifacts` describes the files;
 * `requestDownload` is the only way to obtain a fetchable address, it is a
 * POST because it writes, and portal-api records the attempt whether it grants
 * or refuses. See `apps/server/src/routes/releases.ts`.
 *
 * Moved from `downloads/` on 2026-09-14, when the Downloads page became the
 * Releases pages (the command center's folder grid + release page, read-only).
 */
export interface ReleaseRow {
  id: number;
  version: string;
  status: string;
  release_date: string | null;
  released: boolean;
  description: string | null;
  /** The change log, as Markdown — what a VERSION shipped. portal-api's
   * `ReleaseSerializer` sends it on every row; a line's is usually blank.
   * Since 2026-09-16 this is the ONE copy of a version's release notes: the
   * `release_notes` ARTIFACT kind is gone, and this column is what the page
   * renders. Not to be confused with it — this is a release COLUMN. */
  release_notes?: string | null;
  product_name: string | null;
  /** The two-level hierarchy (2026-09-12): `null` on a release LINE (10.2),
   * set on a BUILD shipped under one (10.2.0.27). A build is what you
   * install; a line is what the guides are written against. */
  parent_release_id: number | null;
}

/** The two downloadable files of a version. `release_notes` was retired on
 *  2026-09-16 — portal-api's `ReleaseArtifact::KINDS` holds these two alone,
 *  and a download naming anything else is refused. The notes live on the
 *  release row's `release_notes` change log instead. */
export type ArtifactKind = "disk1" | "disk2";

export interface ReleaseArtifact {
  id: number;
  kind: ArtifactKind;
  label: string;
  filename: string | null;
  byte_size: number | null;
  checksum: string | null;
  notes: string | null;
  active: boolean;
  restricted: boolean;
}

/** A document that ships WITH a release — an installation guide, a management
 *  guide, the change log — already narrowed upstream to what this reseller
 *  may read. Opened in Documentation's article reader, like any document. */
export interface ReleaseDoc {
  id: number;
  title: string;
  slug: string;
  doc_type: string;
  summary?: string | null;
  category?: { id: number; name: string; slug: string } | null;
  access?: {
    scope: string;
    features?: { slug: string; name: string | null }[];
    products?: { code: string; name: string | null }[];
  } | null;
}

export interface DownloadTicket {
  url: string;
  kind: ArtifactKind;
  filename: string | null;
  byte_size: number | null;
  checksum: string | null;
  expires_in: number;
}

/** Thrown with the SERVER's own refusal text. The server already decided and
 *  already logged it; a friendlier local message would only disagree with the
 *  download log. */
export class DownloadRefused extends Error {}

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const message =
      (body as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
    throw new DownloadRefused(message);
  }
  return body as T;
}

export async function listReleases(): Promise<ReleaseRow[]> {
  const res = await fetch("/api/releases?per_page=100", { credentials: "include" });
  const body = await readJson<{ data?: ReleaseRow[] }>(res);
  return body.data ?? [];
}

export async function listReleaseDocuments(releaseId: number): Promise<ReleaseDoc[]> {
  const res = await fetch(`/api/releases/${releaseId}/documents`, { credentials: "include" });
  const body = await readJson<{ data?: ReleaseDoc[] }>(res);
  return body.data ?? [];
}

export async function listArtifacts(releaseId: number): Promise<ReleaseArtifact[]> {
  const res = await fetch(`/api/releases/${releaseId}/artifacts`, { credentials: "include" });
  const body = await readJson<{ data?: ReleaseArtifact[] }>(res);
  return body.data ?? [];
}

export async function requestDownload(
  releaseId: number,
  kind: ArtifactKind,
): Promise<DownloadTicket> {
  const res = await fetch(`/api/releases/${releaseId}/artifacts/${kind}/download`, {
    method: "POST",
    credentials: "include",
  });
  const body = await readJson<{ data: DownloadTicket }>(res);
  return body.data;
}

/**
 * Hand the browser a granted download URL.
 *
 * NOT `window.location.assign`: assigning `location` replaces the current
 * document, so a URL the browser does not treat as a download takes the portal
 * with it and the reader's only way back is the back button. A detached
 * `target="_blank"` anchor starts the transfer without touching the page.
 */
export function startDownload(url: string, filename?: string | null): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noreferrer";
  // A hint only — a cross-origin response's own Content-Disposition wins.
  if (filename) anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Human byte size. Disk images are measured in GB, so this has to go there. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** Newest-first by SEMANTIC version — "10.1.0" must outrank "9.3.0", which a
 *  string sort gets backwards. portal-api already orders this way; the client
 *  repeats it so a page assembled from more than one fetch cannot disagree. */
export function compareVersionsDesc(a: string, b: string): number {
  const parse = (v: string): number[] =>
    v.split("-")[0]!.split(".").map((p) => Number.parseInt(p, 10) || 0);
  const left = parse(a);
  const right = parse(b);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (right[i] ?? 0) - (left[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
